import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Viewer3D from './components/Viewer3D';
import { getProperty } from '../../services/propertyService';
import ExitInquiryModal from './components/ExitInquiryModal';
import { prefetchAssets, getNetworkQuality } from '../../services/assetCacheService';
import { Lock, Play, ArrowLeft, Box } from 'lucide-react'; // Ajout d'icônes premium

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isGlbFile = (url) => {
  if (!url || typeof url !== 'string') return false;
  const path = url?.toLowerCase()?.split('?')?.[0];
  return path?.endsWith('.glb') || path?.endsWith('.gltf');
};

const extractGlbUrls = (property) => {
  const urls = [];
  if (!property) return urls;
  const videos = Array.isArray(property?.videos) ? property?.videos : [];
  videos?.forEach((v) => {
    const url = v?.url || v;
    if (isGlbFile(url)) urls?.push({ label: v?.label || v?.title || 'Modèle 3D', url });
  });
  return urls;
};

// ─── Premium Lock Overlay ─────────────────────────────────────────────────────
const PremiumLock = ({ onUnlock }) => (
  <div className="absolute inset-0 bg-[#050505]/90 backdrop-blur-xl z-[100] flex flex-col items-center justify-center p-6">
    
    {/* Animated ring & Icon */}
    <div className="relative mb-8 flex items-center justify-center">
      <div className="absolute inset-[-16px] w-24 h-24 rounded-full border-2 border-brand-gold/20 animate-[spin_8s_linear_infinite]" />
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-gold/20 to-brand-gold/5 border border-brand-gold/40 flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.2)]">
        <Lock size={32} className="text-brand-gold" />
      </div>
    </div>

    <h2 className="text-brand-gold text-2xl font-heading font-bold tracking-widest mb-2 text-center uppercase">
      Visite Premium
    </h2>
    <p className="text-white/50 text-sm font-light text-center max-w-[280px] leading-relaxed mb-8">
      Accédez à la visite immersive 3D complète avec un abonnement Premium Kapital States.
    </p>

    <div className="flex flex-col gap-4 w-full max-w-[280px]">
      <button
        onClick={onUnlock}
        className="w-full bg-brand-gold text-black border-none rounded-xl py-3.5 px-6 font-bold text-sm tracking-wide shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] hover:bg-yellow-400 hover:-translate-y-0.5 transition-all"
      >
        ✦ S'ABONNER PREMIUM
      </button>
      <button
        onClick={onUnlock}
        className="w-full bg-transparent border border-brand-gold/30 rounded-xl py-3 px-6 text-brand-gold text-sm hover:bg-brand-gold/10 transition-colors"
      >
        Essai gratuit 7 jours
      </button>
    </div>
  </div>
);

// ─── Enter Button Screen ──────────────────────────────────────────────────────
const EnterScreen = ({ property, modelUrl, onEnter, isPremium }) => {
  return (
    <div className="relative w-full h-full overflow-hidden bg-[#050505]">
      
      {/* Background image */}
      {property?.images?.[0] && (
        <img
          src={property?.images?.[0]}
          alt={property?.title || 'Propriété'}
          className="absolute inset-0 w-full h-full object-cover opacity-20 blur-sm scale-105"
        />
      )}
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/80 to-[#050505]" />
      
      {/* Animated grid lines (Subtle) */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(212,175,55,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} 
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full p-6 text-center">
        
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/30 rounded-full px-4 py-1.5 mb-6 shadow-[0_0_15px_rgba(212,175,55,0.15)]">
          <div className="w-1.5 h-1.5 rounded-full bg-brand-gold shadow-[0_0_8px_#D4AF37]" />
          <span className="text-brand-gold text-[10px] font-bold tracking-widest uppercase">
            Visite 3D Immersive
          </span>
        </div>

        <h1 className="text-white text-3xl md:text-5xl font-heading font-bold mb-4 leading-tight tracking-tight max-w-3xl">
          {property?.title || 'Propriété d\'exception'}
        </h1>

        <p className="text-white/50 text-base font-light mb-10 max-w-md leading-relaxed">
          Explorez chaque pièce librement en vue première personne. Déplacez-vous comme dans la réalité.
        </p>

        {/* Feature pills */}
        <div className="flex flex-wrap gap-3 justify-center mb-12 max-w-lg">
          {['360° Rotation', 'Déplacement libre', 'Textures réalistes', 'Mobile & Desktop']?.map((feat) => (
            <span key={feat} className="bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-white/60 text-xs font-light tracking-wide backdrop-blur-sm">
              {feat}
            </span>
          ))}
        </div>

        {/* Enter button */}
        <button
          onClick={onEnter}
          className="group flex items-center gap-3 bg-brand-gold text-black border-none rounded-2xl py-4 px-10 font-bold text-sm tracking-widest shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] hover:bg-yellow-400 hover:scale-105 transition-all duration-300"
        >
          <Play size={18} className="fill-black" />
          ENTRER DANS LA VISITE
        </button>

        <p className="text-white/30 text-[10px] uppercase tracking-widest mt-8">
          Cliquez pour activer les contrôles · ZQSD pour se déplacer
        </p>
      </div>
      
      {/* Premium lock overlay */}
      {!isPremium && <PremiumLock onUnlock={() => {}} />}
    </div>
  );
};

// ─── Model Selector ───────────────────────────────────────────────────────────
const ModelSelector = ({ models, selected, onSelect }) => {
  if (models?.length <= 1) return null;
  return (
    <div className="absolute top-6 left-6 z-50 flex flex-col gap-2">
      {models?.map((m, i) => {
        const isActive = selected?.url === m?.url;
        return (
          <button
            key={i}
            onClick={() => onSelect(m)}
            className={`px-4 py-2.5 rounded-xl text-xs font-medium tracking-wide backdrop-blur-md transition-all border text-left max-w-[200px] truncate ${
              isActive 
                ? 'bg-brand-gold/10 border-brand-gold/50 text-brand-gold shadow-[0_0_15px_rgba(212,175,55,0.15)]' 
                : 'bg-[#111111]/80 border-white/10 text-white/50 hover:text-white hover:bg-[#1A1A1A]/90'
            }`}
          >
            {m?.label}
          </button>
        );
      })}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ImmersivePropertyTour = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [glbModels, setGlbModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isEntered, setIsEntered] = useState(false);
  const [isPremium] = useState(true); // Set to false to enable premium lock
  const [showExitModal, setShowExitModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!propertyId) { setLoading(false); return; }
      try {
        const res = await getProperty(propertyId);
        if (res?.data) {
          setProperty(res?.data);
          const models = extractGlbUrls(res?.data);
          setGlbModels(models);
          if (models?.length > 0) {
            setSelectedModel(models?.[0]);
            // Prefetch all model URLs into cache — stagger more on slow connections
            const quality = getNetworkQuality();
            const stagger = quality === 'slow' ? 600 : quality === 'medium' ? 300 : 150;
            prefetchAssets(models?.map((m) => m?.url)?.filter(Boolean), stagger);
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [propertyId]);

  const handleEnter = () => setIsEntered(true);
  const handleExit = () => {
    setShowExitModal(true);
  };
  const handleModalClose = () => {
    setShowExitModal(false);
    setIsEntered(false);
  };
  const handleContinueTour = () => {
    setShowExitModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-brand-gold/20 border-t-brand-gold rounded-full animate-spin mx-auto mb-6" />
          <p className="text-brand-gold/70 text-xs font-bold uppercase tracking-widest">
            Chargement de l'environnement...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{property?.title ? `Visite 3D — ${property?.title}` : 'Visite 3D Immersive'} | Kapital States</title>
        <meta name="description" content="Visitez cette propriété en 3D immersif avec vue première personne." />
      </Helmet>
      
      <div className="fixed inset-0 bg-[#050505] flex flex-col overflow-hidden">
        
        {/* Top bar (only when not in viewer) */}
        {!isEntered && (
          <div className="absolute top-0 left-0 right-0 h-16 bg-[#050505]/80 border-b border-white/5 flex items-center justify-between px-6 z-50 backdrop-blur-md">
            <button
              onClick={() => navigate(propertyId ? `/individual-property-page/${propertyId}` : '/property-search-listings')}
              className="flex items-center gap-2 bg-transparent border border-white/10 rounded-lg px-4 py-2 text-white/60 text-xs font-medium hover:text-white hover:bg-white/5 transition-colors"
            >
              <ArrowLeft size={14} /> Retour
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-brand-gold shadow-[0_0_8px_#D4AF37]" />
              <span className="text-brand-gold text-xs font-bold uppercase tracking-[0.2em]">
                KAPITAL STATES
              </span>
            </div>
            
            {/* Espace vide pour centrer le logo */}
            <div className="w-24" /> 
          </div>
        )}

        {/* Main content area */}
        <div className={`flex-1 relative ${isEntered ? 'mt-0' : 'mt-16'}`}>
          {!isEntered ? (
            <EnterScreen
              property={property}
              modelUrl={selectedModel?.url}
              onEnter={handleEnter}
              isPremium={isPremium}
            />
          ) : (
            <>
              {selectedModel?.url ? (
                <Viewer3D modelUrl={selectedModel?.url} onExit={handleExit} />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-6 bg-[#050505]">
                  <div className="w-20 h-20 rounded-3xl bg-white/5 flex items-center justify-center border border-white/10">
                    <Box size={32} className="text-white/30" />
                  </div>
                  <p className="text-white/50 text-sm font-light">
                    Aucun modèle 3D disponible pour cette propriété.
                  </p>
                  <button
                    onClick={handleExit}
                    className="bg-brand-gold/10 border border-brand-gold/30 rounded-xl px-6 py-2.5 text-brand-gold text-xs font-bold uppercase tracking-widest hover:bg-brand-gold hover:text-black transition-colors mt-4"
                  >
                    Retour
                  </button>
                </div>
              )}
              {/* Model selector overlay */}
              <ModelSelector models={glbModels} selected={selectedModel} onSelect={setSelectedModel} />
            </>
          )}
        </div>

        {/* Exit inquiry modal */}
        {showExitModal && (
          <ExitInquiryModal
            property={property}
            onClose={handleModalClose}
            onContinueTour={handleContinueTour}
          />
        )}
      </div>
    </>
  );
};

export default ImmersivePropertyTour;